import React, { useEffect, useMemo, useRef, useState } from 'react';
import Ably from 'ably';
import { ChatClient, type ChatMessageEvent, type Room, Message, OrderBy } from '@ably/chat';
import { getSupabaseClient } from '../../lib/supabase';

type UiMessage = {
  id: string;
  text: string;
  time: string;
  timestamp: number;
  fromMe?: boolean;
  serial?: string;
  deleted?: boolean;
};

type ConnectionState = 'connecting' | 'connected' | 'failed';

type AdminSupportChatProps = {
  targetUserId: string;
  userName: string;
  userRole: string;
  onBack: () => void;
};

const brandPrimary = '#2C2731';
const brandSurface = '#FFFCEF';

export function AdminSupportChat({ targetUserId, userName, userRole, onBack }: AdminSupportChatProps) {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState<ConnectionState>('connecting');
  const [occupancyCount, setOccupancyCount] = useState<number>(0);

  const ablyRef = useRef<Ably.Realtime | null>(null);
  const chatClientRef = useRef<ChatClient | null>(null);
  const roomRef = useRef<Room | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const supabase = useMemo(() => getSupabaseClient(), []);

  // Clear admin unread on mount
  useEffect(() => {
    (async () => {
      try {
        await supabase.functions.invoke('clear-support-notification', {
          body: { target_user_id: targetUserId }
        });
      } catch (err) {
        console.warn('Failed to clear support notifications', err);
      }
    })();
  }, [supabase, targetUserId]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribeConnection: (() => void) | undefined;
    let unsubscribeMessages: (() => void) | undefined;

    const serializeMessage = (
      input: ChatMessageEvent | Message
    ): UiMessage | null => {
      const msg = 'message' in input ? input.message : input;
      if (!msg) return null;

      const timestamp = msg.timestamp ?? Date.now();

      return {
        id: msg.id ?? `${timestamp}-${Math.random()}`,
        text: msg.text ?? '',
        time: new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        timestamp,
        fromMe: msg.clientId?.startsWith('admin:'),
        serial: msg.serial,
        deleted: false,
      };
    };

    (async () => {
      try {
        setConnected('connecting');

        const { data, error } = await supabase.functions.invoke<{
          tokenRequest: Ably.Types.TokenRequest;
          roomName: string;
        }>('support-chat-token', {
          body: { target_user_id: targetUserId, target_user_role: userRole }
        });

        if (error || !data) {
          throw error ?? new Error('Token request failed');
        }

        if (cancelled) return;

        const { tokenRequest, roomName } = data;

        const ably = new Ably.Realtime({
          authCallback: (_params, cb) => cb(null, tokenRequest)
        });
        ablyRef.current = ably;

        const onConnectionChange = (stateChange: Ably.Types.ConnectionStateChange) => {
          if (stateChange.current === 'connected') {
            setConnected('connected');
          } else if (
            stateChange.current === 'failed' ||
            stateChange.current === 'closing' ||
            stateChange.current === 'closed' ||
            stateChange.current === 'suspended'
          ) {
            setConnected('failed');
          }
        };

        ably.connection.on(onConnectionChange);
        unsubscribeConnection = () => ably.connection.off(onConnectionChange);

        const chatClient = new ChatClient(ably);
        chatClientRef.current = chatClient;

        const room = await chatClient.rooms.get(roomName);
        if (cancelled) return;

        roomRef.current = room;
        await room.attach();

        try {
          const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const history = await room.messages.history({
            start: sevenDaysAgo,
            orderBy: OrderBy.OldestFirst,
            limit: 200
          });
          if (!cancelled) {
            const historyMessages = history.items
              .map(serializeMessage)
              .filter((m): m is UiMessage => Boolean(m))
              .sort((a, b) => a.timestamp - b.timestamp);
            setMessages(historyMessages);
          }
        } catch (historyErr) {
          console.warn('Admin support chat: failed to load history', historyErr);
        }

        const subscription = room.messages.subscribe((evt: ChatMessageEvent) => {
          const mapped = serializeMessage(evt);
          if (!mapped) return;
          setMessages((prev) => {
            if (prev.some((msg) => msg.id === mapped.id)) {
              return prev;
            }
            const next = [...prev, mapped];
            next.sort((a, b) => a.timestamp - b.timestamp);
            return next;
          });
        });
        unsubscribeMessages = () => subscription.unsubscribe();

        await room.presence.enter({ role: 'admin' });
        if (!cancelled) {
          setConnected('connected');
        }
      } catch (err) {
        console.error('Admin support chat initialisation failed', err);
        if (!cancelled) {
          setConnected('failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeConnection?.();
      unsubscribeMessages?.();

      (async () => {
        try {
          await roomRef.current?.presence.leave();
        } catch (err) {
          console.warn('Admin support chat: failed to leave presence', err);
        }
        try {
          if (roomRef.current) {
            await chatClientRef.current?.rooms.release(roomRef.current.name);
          }
        } catch (err) {
          console.warn('Admin support chat: failed to release room', err);
        }
        try {
          ablyRef.current?.close();
        } catch (err) {
          console.warn('Admin support chat: failed to close connection', err);
        }
        roomRef.current = null;
        chatClientRef.current = null;
        ablyRef.current = null;
      })();
    };
  }, [supabase, targetUserId]);

  // Poll presence
  useEffect(() => {
    if (!roomRef.current || connected !== 'connected') return;

    let cancelled = false;

    const pollOccupancy = async () => {
      if (cancelled || !roomRef.current || connected !== 'connected') return;
      try {
        const presenceMembers = await roomRef.current?.presence.get();
        if (cancelled) return;
        setOccupancyCount(presenceMembers?.length ?? 0);
      } catch {
        if (cancelled) return;
        setOccupancyCount(1);
      }
    };

    pollOccupancy();
    const intervalId = setInterval(pollOccupancy, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [connected]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    setInput('');
    try {
      await roomRef.current?.messages.send({ text: trimmed });

      // Always notify to keep conversation metadata updated
      try {
        await supabase.functions.invoke('support-message-notification', {
          body: {
            target_user_id: targetUserId,
            preview: trimmed.slice(0, 100),
            other_is_present: occupancyCount > 1,
          }
        });
      } catch (notifErr) {
        console.warn('Failed to send support notification', notifErr);
      }
    } catch (err) {
      console.error('Failed to send admin support message', err);
      setInput(trimmed);
    }
  };

  const roleLabel = userRole === 'guide' ? 'Guide' : 'Seeker';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: brandSurface }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: brandPrimary, color: brandSurface }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: `1px solid rgba(255,252,239,0.3)`, borderRadius: 8, color: brandSurface, cursor: 'pointer', padding: '4px 10px', fontSize: 16 }}
        >
          {'\u2190'}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{userName}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {roleLabel} · {connected === 'connected'
              ? occupancyCount >= 2 ? 'Online now' : 'Offline'
              : connected === 'connecting' ? 'Connecting...' : 'Offline'}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {connected === 'failed' && (
          <p style={{ textAlign: 'center', color: '#DC2626', fontSize: 13, background: '#FEF2F2', padding: '8px 12px', borderRadius: 8, marginBottom: 16 }}>
            Couldn't connect to chat. Please try again.
          </p>
        )}
        {connected !== 'failed' && messages.length === 0 && (
          <p style={{ textAlign: 'center', color: 'rgba(44,39,49,0.5)', fontSize: 13, marginBottom: 16 }}>
            No messages yet. Start the conversation.
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {messages.map((message) => {
            const isFromAdmin = message.fromMe;
            return (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  flexDirection: isFromAdmin ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: '#E8DFBD',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 600, color: brandPrimary, flexShrink: 0,
                  border: '1px solid rgba(44,39,49,0.2)'
                }}>
                  {isFromAdmin ? 'Ad' : roleLabel.slice(0, 2)}
                </div>
                <div style={{ maxWidth: 320 }}>
                  <div style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 13,
                    background: message.deleted
                      ? 'rgba(44,39,49,0.1)'
                      : isFromAdmin ? brandPrimary : '#E8DFBD',
                    color: message.deleted
                      ? 'rgba(44,39,49,0.4)'
                      : isFromAdmin ? brandSurface : brandPrimary,
                    fontStyle: message.deleted ? 'italic' : 'normal',
                  }}>
                    {message.text}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(44,39,49,0.5)', marginTop: 4, textAlign: isFromAdmin ? 'right' : 'left' }}>
                    {message.time}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Input */}
      <div style={{ borderTop: '1px solid rgba(44,39,49,0.15)', padding: '12px 16px', display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={connected !== 'connected'}
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${brandPrimary}`, background: '#E8DFBD',
            fontSize: 13, color: brandPrimary, outline: 'none',
          }}
        />
        <button
          onClick={handleSend}
          disabled={connected !== 'connected'}
          style={{
            padding: '8px 16px', borderRadius: 8,
            border: `1px solid ${brandPrimary}`, background: brandPrimary,
            color: brandSurface, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            opacity: connected !== 'connected' ? 0.5 : 1,
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
